import React, { useMemo } from "react";
import {
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useAuth } from "../contexts/AuthContext";
import LocationMapPigeon2 from "../components/LocationMapPigeon2";
import theme from "../components/Theme";
import { ThemeProvider } from "@mui/material/styles";
import { usePlacesFilters } from "../hooks/usePlacesFilters";
import { usePlacesData, PlaceFilters } from "../hooks/usePlacesData";
import { useUserFavorites } from "../hooks/useUserFavorites";
import { FilterPanel } from "../components/FilterPanel";
import { FilterChips } from "../components/FilterChips";
import { useUiStore } from "../stores/uiStore";

const ListPlaces = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // <768px
  const { authToken, isAuthenticated, userId } = useAuth();
  const openFilterModal = useUiStore((state) => state.openFilterModal);
  
  // Fetch favorites once on route load
  const { data: favorites = [] } = useUserFavorites(userId);

  // Hooks for filter management
  const { filters } = usePlacesFilters();

  /**
   * Convert UI store filter format to API filter format.
   * Arrays become comma-separated strings, null values are omitted.
   */
  const buildApiFilters = useMemo((): PlaceFilters => {
    const apiFilters: PlaceFilters = {};
    if (filters.type) apiFilters.type = filters.type;
    if (filters.age_category.length > 0) apiFilters.age_category = filters.age_category.join(',');
    if (filters.tags.length > 0) apiFilters.tags = filters.tags.join(',');
    if (filters.kid_friendly === true) apiFilters.kid_friendly = true;
    if (filters.disability_friendly === true) apiFilters.disability_friendly = true;
    if (filters.country) apiFilters.country = filters.country;
    if (filters.rating !== null) apiFilters.rating = filters.rating;
    return apiFilters;
  }, [filters]);

  // Fetch places data with TanStack Query
  const { places, isLoading, error } = usePlacesData(buildApiFilters);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(80vh)', overflow: 'hidden' }}>
        {/* Mobile: Filter button in header */}
        {isMobile && (
          <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton
              color="primary"
              onClick={openFilterModal}
              sx={{ minWidth: '48px', minHeight: '48px' }}
              aria-label="Open filters"
            >
              <FilterListIcon />
            </IconButton>
          </Box>
        )}

        {/* Content Section - Filter Panel (30%) + Map (70%) */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          gap: { xs: 2, md: 3 }, 
          px: { xs: 2, md: 3 }, 
          pt: { xs: 1, md: 2 },
          pb: 0,
          height: { xs: 'auto', md: 'calc(100vh - 64px)' }, // Account for AppBar (64px)
          overflow: 'hidden'
        }}>
          {/* Filter Panel Section - 30% on desktop, drawer on mobile */}
          <Box sx={{ 
            flex: { xs: 'none', md: '0 0 30%' }, 
            width: { xs: '100%', md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflowY: { xs: 'visible', md: 'auto' },
            height: { xs: 'auto', md: '100%' }
          }}>
            {!isMobile && (
              <>
                <FilterPanel />
                <FilterChips />
              </>
            )}
            {isMobile && (
              <>
                <FilterPanel />
                <FilterChips />
              </>
            )}
          </Box>

          {/* Map Section - 70% on desktop, full width on mobile */}
          <Box sx={{ 
            flex: { xs: 'none', md: '0 0 70%' }, 
            width: { xs: '100%', md: 'auto' }, 
            height: { xs: '50vh', md: '100%' }, 
            borderRadius: 1, 
            overflow: 'hidden'
          }}>
            <LocationMapPigeon2
              places={places}
              onMarkerClick={(place) => {}}
              height={"100%"}
              favorites={favorites}
            />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ListPlaces;
